import React, { memo, useCallback } from 'react';

/**
 * WorkspaceCard
 * A single app tile: large icon, name, optional unread badge,
 * favorite pin, hover-lift, ripple-on-click and drag reordering support.
 */
const WorkspaceCard = ({
  app,
  active,
  draggable = false,
  isDragging = false,
  isDropTarget = false,
  onOpen,
  onToggleFavorite,
  onContextMenu,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
}) => {
  const handleClick = useCallback(
    (event) => {
      // Ripple effect
      const target = event.currentTarget;
      const circle = document.createElement('span');
      const diameter = Math.max(target.clientWidth, target.clientHeight);
      circle.className = 'ws-ripple';
      circle.style.width = circle.style.height = `${diameter}px`;
      const rect = target.getBoundingClientRect();
      circle.style.left = `${event.clientX - rect.left - diameter / 2}px`;
      circle.style.top = `${event.clientY - rect.top - diameter / 2}px`;
      target.appendChild(circle);
      setTimeout(() => circle.remove(), 560);

      onOpen(app);
    },
    [app, onOpen]
  );

  const handlePin = useCallback(
    (event) => {
      event.stopPropagation();
      onToggleFavorite(app);
    },
    [app, onToggleFavorite]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen(app);
      }
    },
    [app, onOpen]
  );

  const classes = [
    'ws-card',
    active ? 'is-active' : '',
    isDragging ? 'is-dragging' : '',
    isDropTarget ? 'is-drop-target' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      title={app.description}
      aria-label={`${app.name}${app.unread ? `, ${app.unread} unread` : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onContextMenu={(e) => onContextMenu?.(e, app)}
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart?.(e, app) : undefined}
      onDragEnter={draggable ? (e) => onDragEnter?.(e, app) : undefined}
      onDragOver={draggable ? (e) => e.preventDefault() : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      onDrop={draggable ? (e) => onDrop?.(e, app) : undefined}
    >
      {app.unread > 0 && (
        <span className="ws-card-badge">{app.unread > 99 ? '99+' : app.unread}</span>
      )}

      <span className={`ws-card-tile ws-tile-${app.accent || 'blue'}`} aria-hidden="true">
        {app.icon}
      </span>
      <span className="ws-card-name">{app.name}</span>

      <button
        type="button"
        className={`ws-card-pin ${app.isFavorite ? 'is-pinned' : ''}`}
        onClick={handlePin}
        aria-label={app.isFavorite ? `Unpin ${app.name}` : `Pin ${app.name}`}
        title={app.isFavorite ? 'Unpin' : 'Pin to favorites'}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill={app.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
          <path d="M12 2l2.9 6.26 6.1.55-4.6 4.24 1.36 6.4L12 16.9 6.24 19.9l1.36-6.4L3 9.27l6.1-.55L12 2z" />
        </svg>
      </button>
    </div>
  );
};

export default memo(WorkspaceCard);
